import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
    isTabletWidth,
    isSmallTabletWidth,
    isMobileWidth,
} from 'store/resolutions'
import {
    Step,
    SelfcareStep,
    UnlimitedCardStep,
    UnlimitedRenewStep,
    VouchersStep,
} from 'store/general-slice/types'
import { AppMode } from 'helpers/types'
import {
    GetPromotionPresenter,
    PromotionConfirmationPresenter,
} from 'store/api-slice/hooks'
import moment from 'moment'
import { TIMEZONE } from 'constants/system'
import { AbVariantsType } from 'utils/hooks/useAbTesting'

interface IPromotion extends GetPromotionPresenter {
    promotionId?: string
}

interface IAccessability {
    isAppAccessible: boolean
    statusCode?: string | number
}

interface GeneralState {
    isAppAccessible: boolean
    statusCode?: number | string
    promotion: IPromotion
    isTablet: boolean
    isSmallTablet: boolean
    isMobile: boolean
    scrollId: string
    scrollIndex: number
    isMobileSummaryOpened: boolean
    nextStepToEdit:
        | Step
        | SelfcareStep
        | UnlimitedCardStep
        | UnlimitedRenewStep
        | VouchersStep
        | null
    appMode: AppMode
    isErrorModalOpened: boolean
    isCancelYourVisitModalOpen: boolean
    isTrigerringParamInUrl: boolean
    testVariant?: AbVariantsType
    confirmationModal: {
        isConditionConfirmed: boolean
        isModalOpen: boolean
        coupon: string
        details: PromotionConfirmationPresenter
    }
}

const initialState: GeneralState = {
    isAppAccessible: true,
    statusCode: undefined,
    promotion: {},
    isTablet: isTabletWidth(),
    isSmallTablet: isSmallTabletWidth(),
    isMobile: isMobileWidth(),
    scrollId: '',
    scrollIndex: 0,
    isMobileSummaryOpened: false,
    nextStepToEdit: null,
    appMode: null,
    isErrorModalOpened: false,
    isCancelYourVisitModalOpen: false,
    isTrigerringParamInUrl: false,
    confirmationModal: {
        isConditionConfirmed: false,
        isModalOpen: false,
        coupon: '',
        details: {
            active: false,
            buttonApproveText: '',
            buttonDenyText: '',
            description: '',
            header: '',
        },
    },
}

const generalSlice = createSlice({
    name: 'general',
    initialState,
    reducers: {
        setIsTablet(state, action: PayloadAction<boolean>) {
            state.isTablet = action.payload
        },

        setIsMobile(state, action: PayloadAction<boolean>) {
            state.isMobile = action.payload
        },

        setScrollId(state, action: PayloadAction<string>) {
            state.scrollId = action.payload
        },

        setScrollIndex(state, action: PayloadAction<number>) {
            state.scrollIndex = action.payload
        },

        setIsMobileSummaryOpened(state, action: PayloadAction<boolean>) {
            state.isMobileSummaryOpened = action.payload
        },

        setIsErrorModalOpened(state, action: PayloadAction<boolean>) {
            state.isErrorModalOpened = action.payload
        },

        setIsCancelYourVisitModalOpen(state, action: PayloadAction<boolean>) {
            state.isCancelYourVisitModalOpen = action.payload
        },

        setNextStepToEdit(
            state,
            action: PayloadAction<
                | Step
                | SelfcareStep
                | UnlimitedCardStep
                | UnlimitedRenewStep
                | VouchersStep
                | null
            >
        ) {
            state.nextStepToEdit = action.payload
        },

        setAppMode(state, action: PayloadAction<AppMode>) {
            state.appMode = action.payload
        },

        setIsAppAccessible(state, action: PayloadAction<IAccessability>) {
            const { isAppAccessible, statusCode } = action.payload
            state.isAppAccessible = isAppAccessible
            state.statusCode = statusCode
        },

        setPromotion(state, action: PayloadAction<IPromotion>) {
            const { startsAt, endsAt } = action.payload

            state.promotion = action.payload

            if (startsAt && endsAt) {
                // TODO: move times to params
                const currentTime = moment()
                const startTime = moment(startsAt).utcOffset(TIMEZONE)
                const endTime = moment(endsAt).utcOffset(TIMEZONE)
                const bufferedStart = moment(startsAt)
                    .utcOffset(TIMEZONE)
                    .add(30, 'minutes')
                const bufferedEnd = moment(endsAt)
                    .utcOffset(TIMEZONE)
                    .add(30, 'minutes')

                // calculate active state only based on dates
                // during 30 min time buffer at the start and at the end of promotion
                // just to avoid api cache
                if (
                    (currentTime.isSameOrAfter(startTime) &&
                        currentTime.isBefore(bufferedStart)) ||
                    (currentTime.isAfter(endTime) &&
                        currentTime.isBefore(bufferedEnd))
                ) {
                    state.promotion.isActive = currentTime.isBetween(
                        startTime,
                        endTime
                    )
                }
            }
        },

        setTestVariant(state, action: PayloadAction<AbVariantsType>) {
            state.testVariant = action.payload
        },
        setIsConditionConfirmed(state, action: PayloadAction<boolean>) {
            state.confirmationModal.isConditionConfirmed = action.payload
        },
        setIsConfirmationModalOpen(state, action: PayloadAction<boolean>) {
            state.confirmationModal.isModalOpen = action.payload
        },
        setConfirmationModalDetails(
            state,
            action: PayloadAction<PromotionConfirmationPresenter>
        ) {
            state.confirmationModal.details = action.payload
        },
    },
})

export const {
    setIsTablet,
    setIsMobile,
    setScrollId,
    setScrollIndex,
    setIsMobileSummaryOpened,
    setNextStepToEdit,
    setAppMode,
    setIsAppAccessible,
    setPromotion,
    setIsErrorModalOpened,
    setIsCancelYourVisitModalOpen,
    setTestVariant,
    setIsConditionConfirmed,
    setIsConfirmationModalOpen,
    setConfirmationModalDetails,
} = generalSlice.actions

export default generalSlice.reducer
